import React, {
  useState, useRef, useContext, useEffect, useMemo, memo,
} from 'react'
import PropTypes from 'prop-types'
import { useMutation, useQuery } from '@apollo/react-hooks'
import {
  MenuContainer,
  ProductCategoryTabs,
  Tab,
  ProductList,
} from './styled'
import { productCategories as productCategoriesQuery } from '../../../graphql/queries'
import { addToBasket as addToBasketMutation } from '../../../graphql/mutations'
import ProductItem from './ProductItem'
import { formatBasketItemInput, formatPrice } from '../../../utils/formatter'
import { reportError } from '../../../utils/error'
import { SessionContext } from '../../../providers/SessionContextProvider'
import { addItemToOptimisticBasket } from '../../../utils/optimistic-ui'
import CategoryHeader from './CategoryHeader'
import MenuPlaceholder from './MenuPlaceholder'

function Menu({ storeId, basket, headerHeight }) {
  const { writeCache, user } = useContext(SessionContext)
  const [tabIndex, setTabIndex] = useState(0)
  const [visibleCategories, setVisibleCategories] = useState([])
  const [selectedProduct, setProduct] = useState(null)
  const tabBarRef = useRef()
  const sectionRefs = useRef([]).current
  const categoryRefs = useRef([]).current
  const [addToBasket] = useMutation(addToBasketMutation)
  const { data = { productCategories: null } } = useQuery(productCategoriesQuery, { variables: { id: storeId, filter: 'IN_STOCK' } })
  const { productCategories } = data

  const categoryThreshold = useMemo(() => {
    if (tabBarRef.current) {
      return tabBarRef.current.getBoundingClientRect().bottom
    }
    return 100
  }, [tabBarRef.current])

  useEffect(() => {
    document.addEventListener('scroll', syncCategoryTabWhileScrolling, true)
    return () => {
      document.removeEventListener('scroll', syncCategoryTabWhileScrolling)
    }
  }, [visibleCategories])

  useEffect(() => {
    const tab = categoryRefs[tabIndex]
    if (tab) {
      const tabLeft = tab.offsetLeft - ((window.screen.width - tab.offsetWidth) / 2)
      tabBarRef.current.scrollLeft = tabLeft
    }
  }, [tabIndex])

  let timer
  function syncCategoryTabWhileScrolling() {
    if (!visibleCategories.length) return
    if (timer) cancelAnimationFrame(timer)
    timer = requestAnimationFrame(() => {
      const closestCategory = visibleCategories.reduce((closest, index) => {
        const section = { ref: sectionRefs[index], index }
        if (!section.ref) return closest
        const { top: offset, width } = section.ref.getBoundingClientRect()
        if (!closest.offset || offset < closest.offset) {
          return { ...section, offset, width }
        }
        return closest
      }, {})

      if (!closestCategory.ref) return
      if (Math.abs(closestCategory.offset - categoryThreshold) < 100) {
        setTabIndex(closestCategory.index)
      }
    })
  }

  function setVisibleCategory(index, visible) {
    const categories = visible
      ? [...visibleCategories, index]
      : visibleCategories.filter(i => i !== index)
    setVisibleCategories(categories)
  }

  function onClickCategoryTab(index) {
    if (tabBarRef.current) {
      const adjustHeight = index === 0 ? 0 : 5
      const top = sectionRefs[index].offsetTop - tabBarRef.current.clientHeight - headerHeight - adjustHeight
      window.scrollTo({ top })
    }
    setTabIndex(index)
  }

  function handleAddToBasket(values) {
    try {
      const product = formatBasketItemInput({ ...values, product_id: values.id })
      const quantity = basket && (basket.items || []).reduce((sum, item) => item.product_id === product.id ? sum + item.quantity : sum, 0)

      const optimisticItem = {
        ...product,
        id: 'optimistic_basket_item',
        variant_id: 'optimistic_variant',
        extras: product.extras ? product.extras.map(ex => ({ ...ex, __typename: 'Extra' })) : null,
        image: values.image,
        teaser_text: values.teaser_text,
        is_promo: values.is_promo,
        quantity: quantity ? quantity + 1 : 1,
        __typename: 'BasketItem',
      }

      const extrasIncludedPrice = formatPrice(optimisticItem.price, product.extras, true)

      addToBasket({
        variables: { id: basket ? basket.id : null, item: product },
        optimisticResponse: {
          addItemToBasket: addItemToOptimisticBasket(basket, user, storeId, extrasIncludedPrice, optimisticItem),
        },
        update: (proxy, { data: { addItemToBasket: _basket } }) => {
          writeCache({ basket: _basket })
        },
      })

      setProduct(null)
    } catch (error) {
      reportError(error, { message: 'Failed to add an item to basket' })
    }
  }

  function handleClickProduct(item) {
    const nextItem = selectedProduct && selectedProduct.id === item.id ? null : item
    setProduct(nextItem)
  }

  return (
    productCategories ? (
      <MenuContainer>
        <ProductCategoryTabs ref={tabBarRef} headerHeight={headerHeight}>
          {productCategories.map((category, index) => (
            <Tab
              key={category.name}
              ref={(ref) => { categoryRefs[index] = ref }}
              active={tabIndex === index}
              onClick={() => onClickCategoryTab(index)}
            >
              {category.name}
            </Tab>
          ))}
        </ProductCategoryTabs>

        <ProductList>
          {productCategories.map((category, index) => (
            <React.Fragment key={category.name}>
              <div ref={(ref) => { sectionRefs[index] = ref }}>
                <CategoryHeader
                  category={category}
                  sectionRef={sectionRefs[index]}
                  setVisibleCategory={setVisibleCategory}
                  index={index}
                />
              </div>
              {category.products.map(product => (product && (
                <ProductItem
                  key={product.id}
                  category={category}
                  product={product}
                  onClick={handleClickProduct}
                  selected={selectedProduct && selectedProduct.id === product.id}
                  addToBasket={handleAddToBasket}
                  tabBarRef={tabBarRef}
                  headerHeight={headerHeight}
                  basket={basket}
                />
              )))}
            </React.Fragment>
          ))}
        </ProductList>
      </MenuContainer>
    ) : (
      <MenuPlaceholder headerHeight={headerHeight} />
    )
  )
}

Menu.propTypes = {
  storeId: PropTypes.string.isRequired,
  headerHeight: PropTypes.number.isRequired,
  basket: PropTypes.object,
}

Menu.defaultProps = {
  basket: null,
}

export default memo(Menu)
